"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFormState = void 0;
const is_plain_object_1 = require("is-plain-object");
const react_1 = require("react");
const objectField_1 = require("./fields/objectField");
const AutoSaveStatus_1 = require("./AutoSaveStatus/index");
const utils_1 = require("./utils");
// If the user's autoSave hook makes some last-minute `.set` calls to sneak
// in some business logic right before their GraphQL mutation call, ignore it
// so that we don't infinite loop.
let isAutoSaving = false;
// `pendingAutoSave` is a flag for determining if we need to immediately call `maybeAutoSave` again after the initial Promise finishes
// This could happen if a field triggers auto-save while another field's auto-save is already in progress.
let pendingAutoSave = false;
/**
 * Creates a formState instance for editing in a form.
 */
function useFormState(opts) {
    const { config, init, addRules, readOnly = false, loading, autoSave } = opts;
    const autoSaveStatusContext = AutoSaveStatus_1.useAutoSaveStatus();
    // Use a ref so our memo'ized `onBlur` always see the latest value
    const autoSaveRef = react_1.useRef(autoSave);
    autoSaveRef.current = autoSave;
    const firstRunRef = react_1.useRef(true);
    // This is a little weird, but we need to know ahead of time, before the form useMemo, if we're working with classes/mobx proxies
    const [firstInitValue] = react_1.useState(() => utils_1.initValue(config, init));
    const isWrappingMobxProxy = !is_plain_object_1.isPlainObject(firstInitValue);
    // If they're using init.input, useMemo on it (and it might be an array), otherwise allow the identity of init be unstable
    const dep = utils_1.isInput(init) ? makeArray(init.input) : utils_1.isQuery(init) ? [init.query.data, init.query.loading] : [];
    const form = react_1.useMemo(() => {
        function maybeAutoSave() {
            if (isAutoSaving === "in-flight") {
                pendingAutoSave = true;
            }
            else if (isAutoSaving === "queued") {
                return;
            }
            // Don't use canSave() because we don't want to set touched for all the fields
            if (autoSaveRef.current && form.dirty && form.valid && !isAutoSaving) {
                isAutoSaving = "queued";
                let maybeError;
                // We use setTimeout as a cheap way to wait until the end of the current event listener
                setTimeout(async () => {
                    try {
                        // We technically don't flip to in-flight until after the call in case the
                        // user's autoSave function itself wants to call a .set.
                        const promise = autoSaveRef.current(form);
                        isAutoSaving = "in-flight";
                        autoSaveStatusContext.triggerAutoSave();
                        await promise;
                    }
                    catch (e) {
                        maybeError = String(e);
                        throw e;
                    }
                    finally {
                        isAutoSaving = false;
                        autoSaveStatusContext.resolveAutoSave(maybeError === null || maybeError === void 0 ? void 0 : maybeError.toString());
                        if (pendingAutoSave) {
                            pendingAutoSave = false;
                            // Push out the follow-up by 1 tick to allow refreshes to happen to potentially
                            // un-dirty the just-saved data (e.g. if we run right away, the caller's maybeAutoSave
                            // will see a form.changedValue that thinks the just-saved data is still dirty).
                            setTimeout(maybeAutoSave, 0);
                        }
                    }
                }, 0);
            }
        }
        const value = firstRunRef.current ? firstInitValue : utils_1.initValue(config, init);
        const form = objectField_1.createObjectState(config, value, { maybeAutoSave });
        form.readOnly = readOnly;
        setLoading(form, opts);
        // The identity of `addRules` is not stable, but assume that it is for better UX.
        (addRules || (() => { }))(form);
        firstRunRef.current = true;
        return form;
    }, 
    // For this useMemo, we (almost) never re-run so that we can have a stable `form` identity across query refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, ...(isWrappingMobxProxy ? dep : [])]);
    // We use useEffect so that any mutations to the proxies, which will call `setState`s on any observers to
    // queue their components' render), don't happen during our render, per https://fb.me/setstate-in-render.
    react_1.useEffect(() => {
        // Ignore the 1st run b/c our 1st useMemo already initialized `form` with the current `init` value.
        // Also for mobx proxies, we recreate a new form-state every time init changes, so that our
        // fields fundamentally pointing to the right proxy. So just defer to the ^ useMemo.
        if (firstRunRef.current || isWrappingMobxProxy) {
            firstRunRef.current = false;
            return;
        }
        setLoading(form, opts);
        // Note: maybe someday we want to watch the `id` value and notice if it's changing (i.e. 2 -> 3),
        // and treat this more as a reset than a refresh, b/c the id changing means it's probably
        // not "the cache refreshed after a mutation save" but "the cache changed b/c of changing
        // rows in the table that our side panel's form has open/is focused on" (which ideally would
        // be treated as a full component remount by having an `key` field somewhere in the parent
        // component, but it's unlikely the user will always remember to do this).
        form.set(utils_1.initValue(config, init), { refreshing: true });
    }, [form, ...dep]);
    // Use useEffect so that we don't touch the form.init proxy during a render
    react_1.useEffect(() => {
        form.readOnly = readOnly;
        if (loading !== undefined) {
            form.loading = loading;
        }
    }, [form, readOnly, loading]);
    return form;
}
exports.useFormState = useFormState;
function setLoading(form, opts) {
    const { loading, init } = opts;
    if (loading !== undefined) {
        // Prefer the explicit/top-level opts.loading if it's set
        form.loading = loading;
    }
    else if (utils_1.isInput(init) && !init.ifUndefined) {
        // Otherwise, check for `init.input`
        form.loading = init.input === undefined;
    }
    else if (utils_1.isQuery(init)) {
        // Or `query.loading`
        form.loading = init.query.loading;
    }
}
function makeArray(input) {
    return Array.isArray(input) ? input : [input];
}
